import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FinanceTrendChart from '../FinanceTrendChart';
import MonthStepper from '../../../../components/MonthStepper';
import TodoList from './TodoList';
import MonthFigures from './MonthFigures';
import DebtDonut from './DebtDonut';
import CompositionDonut from './CompositionDonut';
import SectionTitle from './SectionTitle';
import { forMonth } from './monthText';
import { OverviewScope } from './scope';
import {
  BalanceBucketsDto,
  FinanceCompositionDto,
  FinanceSummaryDto,
  FinanceTodoItemDto,
  FinanceTrendDto
} from '../../../../common/interfaces/invoicing.interface';

/**
 * A location's or a group's page, laid out as the overview is: to do, the month in figures,
 * the trend beside today's debts, what the month was invoiced for beside `aside`, and then
 * the table one level down.
 */
const ScopeOverview: React.FC<{
  kindergartenId: string;
  scope: OverviewScope;
  month: string;
  onMonthChange: (month: string) => void;
  summary: FinanceSummaryDto;
  trend: FinanceTrendDto | null;
  todo: FinanceTodoItemDto[] | null;
  buckets: BalanceBucketsDto | null;
  composition: FinanceCompositionDto | null;
  onOpenScheme?: () => void;
  aside: React.ReactNode;
  tableTitle: string;
  table: React.ReactNode;
}> = ({
  kindergartenId,
  scope,
  month,
  onMonthChange,
  summary,
  trend,
  todo,
  buckets,
  composition,
  onOpenScheme,
  aside,
  tableTitle,
  table
}) => {
  const { t } = useTranslation();
  return (
    <>
      {todo && <TodoList items={todo} kindergartenId={kindergartenId} scope={scope} onOpenScheme={onOpenScheme} />}

      <Box component="section" aria-labelledby="finance-month-title" sx={{ mb: 3 }}>
        <SectionTitle
          id="finance-month-title"
          aside={
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, justifyContent: 'space-between' }}>
              <MonthStepper value={month} onChange={onMonthChange} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('balances.amountsInRsd')}</Typography>
            </Stack>
          }
        >
          {t('finance.overview.month')}
        </SectionTitle>
        <MonthFigures
          month={month}
          totals={summary.totals}
          points={trend?.points ?? []}
          buckets={buckets}
          kindergartenId={kindergartenId}
          scope={scope}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mb: 3 }}>
        {trend ? <FinanceTrendChart trend={trend} onSelectMonth={onMonthChange} selectedMonth={month} /> : <Box />}
        <DebtDonut buckets={buckets} kindergartenId={kindergartenId} scope={scope} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <CompositionDonut composition={composition} monthLabel={forMonth(t, month)} />
        {aside}
      </Box>

      <Box component="section" aria-labelledby="finance-table-title">
        <SectionTitle id="finance-table-title">{tableTitle}</SectionTitle>
        {table}
      </Box>
    </>
  );
};

export default ScopeOverview;
